// Full-chrome dark layout for /admin/releases (and future admin-console areas).
// Replaces AdminLayout entirely while inside this area. The sidebar's "Platform"
// section links back to the real admin pages so operators can move between the
// OTA work and the rest of admin without losing context.

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import {
  HomeIcon,
  CubeIcon,
  FolderOpenIcon,
  Squares2X2Icon,
  ChartBarIcon,
  ClipboardDocumentListIcon,
  ShieldCheckIcon,
  SignalIcon,
  UsersIcon,
  ChevronRightIcon,
  ArrowPathIcon,
  BellIcon,
  LockClosedIcon,
  DocumentTextIcon,
  ClipboardDocumentCheckIcon,
  BookOpenIcon,
  PhotoIcon,
  Bars3Icon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../providers/AuthProvider';
import { Badge, cx } from '../components/admin/console';

interface NavItem {
  to: string;
  label: string;
  icon: typeof HomeIcon;
  end?: boolean;
}

// Sub-area nav (the OTA console pages).
const releasesNav: NavItem[] = [
  { to: '/admin/releases',           label: 'Overview',         icon: HomeIcon, end: true },
  { to: '/admin/releases/changelog', label: 'Changelog',        icon: DocumentTextIcon },
  { to: '/admin/releases/register',  label: 'Register updates', icon: ClipboardDocumentCheckIcon },
  { to: '/admin/releases/builds',    label: 'Builds',           icon: CubeIcon },
  { to: '/admin/releases/status',    label: 'OTA Status',       icon: FolderOpenIcon },
  { to: '/admin/releases/channels',  label: 'Release channels', icon: Squares2X2Icon },
  { to: '/admin/releases/coverage',  label: 'Fleet coverage',   icon: ChartBarIcon },
  { to: '/admin/releases/log',       label: 'Device check log', icon: ClipboardDocumentListIcon },
  { to: '/admin/releases/audit',     label: 'Audit',            icon: ShieldCheckIcon },
  { to: '/admin/releases/device-channels', label: 'Device overrides', icon: LockClosedIcon },
];

// Help (Scout help articles, docs/wip/SCOUT_HELP.md).
const helpNav: NavItem[] = [
  { to: '/admin/help/articles', label: 'Articles', icon: BookOpenIcon },
  { to: '/admin/help/images', label: 'Images', icon: PhotoIcon },
];

// Backlinks to real admin pages — operators jump back to the rest of admin from here.
const platformNav: NavItem[] = [
  { to: '/admin',          label: 'Dashboard', icon: HomeIcon, end: true },
  { to: '/admin/devices',  label: 'Devices',   icon: SignalIcon },
  { to: '/admin/users',    label: 'Users',     icon: UsersIcon },
  { to: '/admin/packages', label: 'Packages',  icon: CubeIcon },
];

export function AdminConsoleLayout() {
  const { user } = useAuth();
  const role = user?.role ?? 'admin';
  // Phones: the sidebar is a drawer behind the top bar's menu button. It
  // closes on every navigation and on Escape.
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  useEffect(() => setMenuOpen(false), [location.pathname]);
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  return (
    <div className="admin-console flex min-h-screen bg-slate-900 text-slate-300">
      <aside className="hidden md:flex w-60 bg-slate-800 border-r border-slate-700 flex-col flex-shrink-0 sticky top-0 h-screen">
        <SidebarContent user={user} role={role} />
      </aside>

      {menuOpen && (
        <div className="fixed inset-0 z-50 md:hidden" role="dialog" aria-modal="true" aria-label="Menu">
          <button
            type="button"
            className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm"
            aria-label="Close menu"
            onClick={() => setMenuOpen(false)}
          />
          <aside className="relative w-72 max-w-[85vw] h-full bg-slate-800 border-r border-slate-700 flex flex-col">
            <button
              type="button"
              onClick={() => setMenuOpen(false)}
              aria-label="Close menu"
              className="absolute top-2.5 right-2.5 w-11 h-11 inline-flex items-center justify-center rounded-lg text-slate-400 hover:text-white hover:bg-slate-700"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
            <SidebarContent user={user} role={role} />
          </aside>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        <Topbar role={role} onMenu={() => setMenuOpen(true)} menuOpen={menuOpen} />
        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function SidebarContent({ user, role }: { user: ReturnType<typeof useAuth>['user']; role: string }) {
  return (
    <>
      <div className="p-4 border-b border-slate-700 flex items-center gap-2.5">
        <img src="/vortex-logo-white.svg" alt="" className="w-7 h-7" />
        <div className="min-w-0">
          <div className="text-[15px] font-bold text-white tracking-tight leading-none">
            Vortex Marine
          </div>
          <div className="text-[10px] text-purple-400 font-bold tracking-[0.15em] uppercase mt-0.5">
            Admin
          </div>
        </div>
      </div>

      <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
        <SectionLabel>Platform</SectionLabel>
        {platformNav.map((it) => (
          <SidebarLink key={it.to} item={it} variant="platform" />
        ))}

        <SectionLabel className="pt-3">OTA Releases</SectionLabel>
        {releasesNav.map((it) => (
          <SidebarLink key={it.to} item={it} variant="releases" />
        ))}

        <SectionLabel className="pt-3">Help</SectionLabel>
        {helpNav.map((it) => (
          <SidebarLink key={it.to} item={it} variant="releases" />
        ))}
      </nav>

      <div className="p-3 border-t border-slate-700">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center text-white text-xs font-bold">
            {(user?.name ?? user?.email ?? '?').charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-xs font-semibold text-white truncate">
              {user?.name ?? user?.email ?? 'Operator'}
            </div>
            <div className="text-[10px] text-slate-500 font-mono">{role}</div>
          </div>
        </div>
      </div>
    </>
  );
}

function SectionLabel({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cx(
        'px-2 pb-1 pt-2 text-[10px] font-bold tracking-widest uppercase text-slate-500',
        className,
      )}
    >
      {children}
    </div>
  );
}

function SidebarLink({ item, variant }: { item: NavItem; variant: 'platform' | 'releases' }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) =>
        cx(
          'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
          variant === 'releases' && isActive
            ? 'bg-purple-500/10 text-purple-300'
            : 'text-slate-400 hover:bg-slate-700 hover:text-white',
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon
            className={cx(
              'w-[18px] h-[18px]',
              variant === 'releases' && isActive ? 'text-purple-400' : '',
            )}
          />
          <span>{item.label}</span>
        </>
      )}
    </NavLink>
  );
}

function formatRefreshAgo(updatedAtMs: number): string {
  const sec = Math.max(0, Math.floor((Date.now() - updatedAtMs) / 1000));
  if (sec < 5) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  return `${Math.floor(sec / 3600)}h ago`;
}

function useOtaDataUpdatedAt(): number {
  const qc = useQueryClient();
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const unsub = qc.getQueryCache().subscribe(() => setTick((n) => n + 1));
    return unsub;
  }, [qc]);

  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 10_000);
    return () => clearInterval(id);
  }, []);

  void tick;

  const times = qc
    .getQueryCache()
    .findAll({ queryKey: ['ota'] })
    .map((q) => q.state.dataUpdatedAt)
    .filter((t) => t > 0);

  return times.length > 0 ? Math.max(...times) : Date.now();
}

/** The console page you are on, for the breadcrumb: the most specific nav item matching the path. */
function currentPageLabel(pathname: string): string | null {
  const hit = releasesNav
    .filter((it) => (it.end ? pathname === it.to || pathname === `${it.to}/` : pathname === it.to || pathname.startsWith(`${it.to}/`)))
    .sort((a, b) => b.to.length - a.to.length)[0];
  return hit?.label ?? null;
}

function Topbar({ role, onMenu, menuOpen }: { role: string; onMenu: () => void; menuOpen: boolean }) {
  const qc = useQueryClient();
  const page = currentPageLabel(useLocation().pathname);
  const otaFetching = useIsFetching({ queryKey: ['ota'] });
  const dataUpdatedAt = useOtaDataUpdatedAt();

  const handleRefresh = useCallback(async () => {
    await qc.invalidateQueries({ queryKey: ['ota'] });
  }, [qc]);

  return (
    <header className="h-14 border-b border-slate-700 bg-slate-900/80 backdrop-blur-md sticky top-0 z-40 flex items-center px-3 md:px-6 gap-3 md:gap-4">
      <button
        type="button"
        onClick={onMenu}
        aria-label="Open menu"
        aria-expanded={menuOpen}
        className="md:hidden w-11 h-11 -ml-1 inline-flex items-center justify-center rounded-lg text-slate-300 hover:text-white hover:bg-slate-800"
      >
        <Bars3Icon className="w-6 h-6" />
      </button>
      <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-2 text-[13px] text-slate-500 font-medium">
        <span className="hidden sm:inline">Admin</span>
        <ChevronRightIcon className="w-3 h-3 hidden sm:block" aria-hidden="true" />
        <span className={page ? 'hidden sm:inline' : 'text-slate-300'}>OTA Releases</span>
        {page && (
          <>
            <ChevronRightIcon className="w-3 h-3 hidden sm:block" aria-hidden="true" />
            <span className="truncate text-slate-200" aria-current="page">{page}</span>
          </>
        )}
      </nav>
      <div className="flex-1" />
      <button
        type="button"
        onClick={() => void handleRefresh()}
        disabled={otaFetching > 0}
        className="text-slate-400 hover:text-white disabled:opacity-50 text-xs flex items-center gap-1.5"
        title="Refresh"
      >
        <ArrowPathIcon
          className={cx('w-3.5 h-3.5', otaFetching > 0 && 'animate-spin')}
        />
        <span className="hidden sm:inline">Last refreshed {formatRefreshAgo(dataUpdatedAt)}</span>
      </button>
      <button type="button" className="text-slate-400 hover:text-white" title="Notifications">
        <BellIcon className="w-[18px] h-[18px]" />
      </button>
      <Badge tone={role === 'super_admin' ? 'admin' : role === 'admin' ? 'info' : 'warning'}>
        <LockClosedIcon className="w-3 h-3" /> {role}
      </Badge>
    </header>
  );
}

export default AdminConsoleLayout;
